import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { usePurchaseOrder } from '@/api/rpc/hooks/accounts-payable/usePurchaseOrders';
import { PackageIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Badge } from '@/components/ui/badge';

interface PurchaseOrderPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always INTERNAL_PURCHASE_ORDER
  entityId: string;
  className?: string;
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Partially Received',
  FULLY_RECEIVED: 'Fully Received',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  FULLY_INVOICED: 'Fully Invoiced',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'default',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return 'N/A';

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

export function PurchaseOrderPreview({
  entityId,
  className,
}: PurchaseOrderPreviewProps) {
  const {
    data: purchaseOrderData,
    isLoading,
    error,
  } = usePurchaseOrder(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Purchase Order</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !purchaseOrderData?.purchaseOrder) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <PackageIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Purchase Order</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load purchase order details
        </p>
      </div>
    );
  }

  const purchaseOrder = purchaseOrderData.purchaseOrder;
  const lineItems = purchaseOrderData.lineItems || [];

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <PackageIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          {purchaseOrder.externalId || `PO-${purchaseOrder.internalId}`}
        </h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge
            variant={STATUS_VARIANTS[purchaseOrder.status] || 'outline'}
            className="text-xs"
          >
            {STATUS_LABELS[purchaseOrder.status] || purchaseOrder.status}
          </Badge>
        </div>

        {purchaseOrder.totalAmount && (
          <div>
            <span className="text-muted-foreground">Total: </span>
            <span className="font-medium">
              {formatCurrencyValue(
                purchaseOrder.totalAmount,
                purchaseOrder.currency || 'USD',
              )}
            </span>
          </div>
        )}

        <div>
          <span className="text-muted-foreground">Order Date: </span>
          <span className="font-medium">
            {formatDateOnlyString(purchaseOrder.orderDate)}
          </span>
        </div>

        {purchaseOrder.deliveryDate && (
          <div>
            <span className="text-muted-foreground">Delivery Date: </span>
            <span className="font-medium">
              {formatDateOnlyString(purchaseOrder.deliveryDate)}
            </span>
          </div>
        )}

        {lineItems.length > 0 && (
          <div>
            <span className="text-muted-foreground">Line Items: </span>
            <span className="font-medium">{lineItems.length}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/accounts-payable/purchase-orders/${purchaseOrder.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          View Details
        </a>
      </div>
    </div>
  );
}
